import { buildBuySwapDryRun } from './buySwap';
import { buildSellSwapDryRun } from './sellSwap';
import { LEVER_SWAP_CONFIG } from './swapConfig';
import {
  approveMaxIfEnabled,
  buildPreRealExecutionPlan,
  sendPreparedSwapIfEnabled,
} from './swapExecutionPlan';

async function buildLeverSwapBase({ mode, signer, walletAddress, swapMultiplier = LEVER_SWAP_CONFIG.DEFAULT_SWAP_MULTIPLIER }) {
  let result;
  if (mode === 'BUY') result = await buildBuySwapDryRun({ signer, walletAddress, swapMultiplier });
  else if (mode === 'SELL') result = await buildSellSwapDryRun({ signer, walletAddress, swapMultiplier });
  else throw new Error(`Unsupported lever mode: ${mode}`);

  return {
    ...result,
    phase: LEVER_SWAP_CONFIG.REAL_SEND_ENABLED
      ? '5C1_REAL_EXECUTION_REVERT_AWARE'
      : '5C1_REAL_EXECUTION_REVERT_AWARE_DRY_RUN',
    executionPlan: buildPreRealExecutionPlan(result),
  };
}



export async function executeLeverSwapGuarded({ mode, signer, walletAddress, swapMultiplier = LEVER_SWAP_CONFIG.DEFAULT_SWAP_MULTIPLIER, onStep }) {
  const base = await buildLeverSwapBase({ mode, signer, walletAddress, swapMultiplier });
  const executionPlan = base.executionPlan || buildPreRealExecutionPlan(base);

  const realExecution = {
    enabled: Boolean(LEVER_SWAP_CONFIG.REAL_SEND_ENABLED),
    status: LEVER_SWAP_CONFIG.REAL_SEND_ENABLED ? 'ready' : 'disabled',
    approvalTxs: [],
    approvalReceipts: [],
    swapTxHash: null,
    swapReceipt: null,
    swapBlockNumber: null,
    swapNonce: null,
    events: [],
    error: null,
  };

  const result = {
    ...base,
    executionPlan,
    realExecution,
  };

  const addEvent = (event) => {
    const item = { timestamp: new Date().toISOString(), ...event };
    realExecution.events.push(item);
    return item;
  };

  const makeExecutionError = (message, code, cause) => {
    const err = new Error(message);
    err.code = code;
    err.cause = cause;
    err.result = result;
    return err;
  };

  if (!executionPlan.sendReady || !base.validation?.ok) {
    realExecution.status = 'blocked_by_validation';
    addEvent({ step: 'blocked_by_validation', errors: base.validation?.errors || [] });
    throw makeExecutionError(
      `Swap blocked by validation: ${(base.validation?.errors || []).join(' | ') || 'unknown validation error'}`,
      'SWAP_BLOCKED_BY_VALIDATION'
    );
  }

  addEvent({ step: 'precheck_passed', mode, swapMultiplier: base.swapMultiplier || swapMultiplier, needsApproval: executionPlan.needsApproval, approvalsNeeded: executionPlan.approvalsNeeded?.length || 0 });
  onStep?.({ step: 'precheck_passed', result });

  if (!LEVER_SWAP_CONFIG.REAL_SEND_ENABLED) {
    addEvent({ step: 'dry_run_ready' });
    onStep?.({ step: 'dry_run_ready', result });
    return result;
  }

  try {
    for (const approval of executionPlan.approvalsNeeded || []) {
      addEvent({ step: 'approval_requested', label: approval?.label, spender: approval?.spender, tokenAddress: approval?.tokenAddress });
      onStep?.({ step: 'approval_requested', approval, result });

      const receipt = await approveMaxIfEnabled({ signer, approval });
      const approvalHash = receipt?.transactionHash || receipt?.hash || null;
      realExecution.approvalTxs.push(approvalHash);
      realExecution.approvalReceipts.push(receipt);

      addEvent({
        step: 'approval_confirmed',
        label: approval?.label,
        spender: approval?.spender,
        tokenAddress: approval?.tokenAddress,
        txHash: approvalHash,
        blockNumber: receipt?.blockNumber || null,
        status: receipt?.status ?? null,
      });
      onStep?.({ step: 'approval_confirmed', approval, receipt, result });
    }

    addEvent({ step: 'swap_requested', mode, swapMultiplier: base.swapMultiplier || swapMultiplier, to: base.txData?.to, value: String(base.txData?.value || '0') });
    onStep?.({ step: 'swap_requested', result });

    const swapTx = await sendPreparedSwapIfEnabled({ signer, txData: base.txData });
    realExecution.swapTxHash = swapTx.hash;
    realExecution.swapNonce = swapTx.nonce ?? null;
    realExecution.status = 'swap_pending';

    addEvent({ step: 'swap_submitted', txHash: swapTx.hash, nonce: swapTx.nonce ?? null, to: swapTx.to || base.txData?.to, value: String(base.txData?.value || '0') });
    onStep?.({ step: 'swap_submitted', txHash: swapTx.hash, result });

    const receipt = await swapTx.wait();
    realExecution.swapReceipt = receipt;
    realExecution.swapBlockNumber = receipt?.blockNumber || null;
    realExecution.status = receipt?.status === 1 ? 'confirmed' : 'reverted';

    if (receipt?.status === 1) {
      addEvent({ step: 'swap_confirmed', txHash: swapTx.hash, blockNumber: receipt?.blockNumber || null, status: receipt?.status });
      onStep?.({ step: 'swap_confirmed', receipt, result });
      return result;
    }

    addEvent({ step: 'swap_reverted', txHash: swapTx.hash, blockNumber: receipt?.blockNumber || null, status: receipt?.status, reason: 'Receipt status was not successful.' });
    onStep?.({ step: 'swap_reverted', txHash: swapTx.hash, receipt, result });
    throw makeExecutionError('Swap transaction reverted onchain.', 'SWAP_REVERTED', receipt);
  } catch (error) {
    const message = error?.message || String(error);
    const code = error?.code || '';

    if (code === 4001 || code === 'ACTION_REJECTED' || code === 'USER_REJECTED') {
      realExecution.status = 'user_rejected';
      realExecution.error = message;
      addEvent({ step: 'user_rejected_signature', code, message });
      onStep?.({ step: 'user_rejected_signature', error, result });
      throw makeExecutionError('User rejected the wallet confirmation.', 'USER_REJECTED_SIGNATURE', error);
    }

    if (code === 'SWAP_REVERTED' || realExecution.status === 'reverted') {
      realExecution.status = 'reverted';
      realExecution.error = message;
      addEvent({ step: 'swap_reverted', txHash: realExecution.swapTxHash, blockNumber: realExecution.swapBlockNumber, nonce: realExecution.swapNonce, message });
      onStep?.({ step: 'swap_reverted', error, result });
      throw makeExecutionError(message || 'Swap transaction reverted onchain.', 'SWAP_REVERTED', error);
    }

    if (code === 'TRANSACTION_PENDING') {
      realExecution.status = 'swap_pending';
      realExecution.error = message;
      onStep?.({step:'confirmation_pending',txHash:realExecution.swapTxHash,result});
      throw makeExecutionError(message,'TRANSACTION_PENDING',error);
    }
    if(error.receipt?.status===0){
      realExecution.status='reverted';realExecution.swapReceipt=error.receipt;
      onStep?.({step:'swap_reverted',receipt:error.receipt,result});
      throw makeExecutionError('Swap transaction reverted onchain.','SWAP_REVERTED',error);
    }
    realExecution.status = 'failed';
    realExecution.error = message;
    addEvent({ step: 'failed', code, message });
    onStep?.({ step: 'failed', error, result });
    throw makeExecutionError(message, 'SWAP_FAILED', error);
  }
}
